import { pino } from 'pino';
import { REDACTED_PATHS, REDACTION_PLACEHOLDER, scrubPanLikeText } from '@ibe/utils';
/**
 * Structured logging.
 *
 * Wraps pino rather than exposing it directly so that redaction, level
 * vocabulary and the correlation-ID contract are enforced in one place. Every
 * service logs through this; nothing calls `console` (the lint rule makes that
 * an error everywhere except this package).
 */
export const LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'];
class PinoBackedLogger {
    #pino;
    constructor(instance) {
        this.#pino = instance;
    }
    get level() {
        return this.#pino.level;
    }
    trace(message, context) {
        this.#emit('trace', message, context);
    }
    debug(message, context) {
        this.#emit('debug', message, context);
    }
    info(message, context) {
        this.#emit('info', message, context);
    }
    warn(message, context) {
        this.#emit('warn', message, context);
    }
    error(message, context) {
        this.#emit('error', message, context);
    }
    fatal(message, context) {
        this.#emit('fatal', message, context);
    }
    child(bindings) {
        return new PinoBackedLogger(this.#pino.child(bindings));
    }
    async time(message, operation) {
        const startedAt = process.hrtime.bigint();
        try {
            return await operation();
        }
        finally {
            this.#emit('info', message, { durationMs: elapsedMs(startedAt) });
        }
    }
    #emit(level, message, context) {
        // Message text is scrubbed as well as structured fields: a card number
        // pasted into an upstream error string would otherwise bypass path-based
        // redaction entirely.
        this.#pino[level](context ?? {}, scrubPanLikeText(message));
    }
}
const NS_PER_MS = 1000000n;
const MS_PRECISION = 1000;
function elapsedMs(startedAt) {
    const elapsedNs = process.hrtime.bigint() - startedAt;
    return Number((elapsedNs * BigInt(MS_PRECISION)) / NS_PER_MS) / MS_PRECISION;
}
/**
 * Builds a logger.
 *
 * Redaction is applied unconditionally. There is no option to disable it —
 * "just for debugging" is precisely how cardholder data reaches a log file.
 */
export function createLogger(options = {}) {
    const { level = 'info', pretty = false, bindings = {}, destination } = options;
    const pinoOptions = {
        level,
        base: bindings,
        redact: {
            paths: [...REDACTED_PATHS],
            censor: REDACTION_PLACEHOLDER,
        },
        // ISO-8601 UTC, matching the audit-log and API timestamp format.
        timestamp: () => `,"time":"${new Date().toISOString()}"`,
        formatters: {
            level: (label) => ({ level: label }),
        },
        ...(pretty
            ? {
                transport: {
                    target: 'pino-pretty',
                    options: { colorize: true, translateTime: 'HH:MM:ss.l', ignore: 'pid,hostname' },
                },
            }
            : {}),
    };
    return new PinoBackedLogger(destination === undefined ? pino(pinoOptions) : pino(pinoOptions, destination));
}
/**
 * A logger that discards everything.
 *
 * For tests and for library code that accepts an optional logger, so callers
 * never need a null check.
 */
export function createSilentLogger() {
    return new PinoBackedLogger(pino({ level: 'silent' }));
}
//# sourceMappingURL=logger.js.map